// Sistema de RBAC (Role Based Access Control)
import { UserRole, PERMISSIONS, User } from "@/types";

export const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  PRODUCAO: [
    PERMISSIONS.EVENT_CREATE,
    PERMISSIONS.EVENT_READ,
    PERMISSIONS.EVENT_UPDATE,
    PERMISSIONS.EVENT_DELETE,
    PERMISSIONS.PARTICIPANT_IMPORT,
    PERMISSIONS.PARTICIPANT_CREATE,
    PERMISSIONS.PARTICIPANT_READ,
    PERMISSIONS.PARTICIPANT_UPDATE,
    PERMISSIONS.PARTICIPANT_DELETE,
    PERMISSIONS.AGENDA_GENERATE,
    PERMISSIONS.AGENDA_READ_ALL,
    PERMISSIONS.AGENDA_UPDATE,
    PERMISSIONS.MEETING_CREATE,
    PERMISSIONS.MEETING_READ_ALL,
    PERMISSIONS.MEETING_UPDATE_STATUS,
    PERMISSIONS.MEETING_MOVE,
    PERMISSIONS.USER_CREATE,
    PERMISSIONS.USER_READ,
    PERMISSIONS.USER_UPDATE,
    PERMISSIONS.USER_DELETE,
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.BRANDING_UPDATE,
  ],
  AGIS: [
    PERMISSIONS.EVENT_READ,
    PERMISSIONS.EVENT_DELETE,
    PERMISSIONS.PARTICIPANT_READ,
    PERMISSIONS.AGENDA_READ_ALL,
    PERMISSIONS.MEETING_READ_ALL,
    PERMISSIONS.DASHBOARD_VIEW,
  ],
  FABRICANTE: [
    PERMISSIONS.EVENT_READ,
    PERMISSIONS.AGENDA_READ_OWN,
    PERMISSIONS.MEETING_READ_OWN,
    PERMISSIONS.MEETING_UPDATE_STATUS,
  ],
  REVENDA: [
    PERMISSIONS.EVENT_READ,
    PERMISSIONS.AGENDA_READ_OWN,
    PERMISSIONS.MEETING_READ_OWN,
  ],
};

export function hasPermission(user: User | null, permission: string): boolean {
  if (!user) return false;
  
  const rolePermissions = ROLE_PERMISSIONS[user.papel];
  return rolePermissions.includes(permission);
}

export function canAccessRoute(user: User | null, route: string): boolean {
  if (!user) return false;

  // Mapeamento direto de rotas para papéis permitidos
  const routeRoleAccess: Record<string, UserRole[]> = {
    // AGIS só pode acessar: Painel, Agenda e Mapa
    "/painel": ["PRODUCAO", "AGIS"],
    "/eventos": ["PRODUCAO"], // Apenas PRODUCAO acessa gestão de eventos
    "/agenda": ["AGIS", "PRODUCAO"],
    "/agenda-switcher": ["AGIS", "PRODUCAO"], // AGIS acessa via "Agenda" no menu
    "/mapa": ["AGIS", "PRODUCAO", "FABRICANTE", "REVENDA"],
    
    // Rotas RESTRITAS para AGIS
    "/participantes": ["PRODUCAO"], // AGIS não pode acessar
    "/usuarios": ["PRODUCAO"], // AGIS não pode acessar
    "/branding": ["PRODUCAO"], // AGIS não pode acessar
    "/configuracoes": ["PRODUCAO"], // AGIS não pode acessar
    "/configuracao-associacoes": ["PRODUCAO"], // AGIS não pode acessar
    
    // Rotas específicas para outros papéis
    "/minha-agenda": ["FABRICANTE", "REVENDA", "PRODUCAO"],
  };

  // Verificar acesso baseado no papel do usuário
  const allowedRoles = routeRoleAccess[route];
  if (allowedRoles) {
    return allowedRoles.includes(user.papel);
  }

  // Verificar rotas que começam com um path específico (sub-rotas)
  for (const [routePath, roles] of Object.entries(routeRoleAccess)) {
    if (route.startsWith(routePath) && routePath !== "/") {
      return roles.includes(user.papel);
    }
  }

  // Para rotas não mapeadas, usar sistema de permissões como fallback
  const routePermissions: Record<string, string[]> = {
    "/agendas": [PERMISSIONS.AGENDA_READ_ALL, PERMISSIONS.AGENDA_READ_OWN],
  };

  const requiredPermissions = routePermissions[route];
  if (requiredPermissions) {
    return requiredPermissions.some(permission => hasPermission(user, permission));
  }

  // Por padrão, permitir acesso se não há restrição específica
  return true;
}

export function getRedirectPath(role: UserRole): string {
  switch (role) {
    case "PRODUCAO":
      return "/painel";
    case "AGIS":
      return "/agenda-switcher"; // AGIS vai direto para a agenda (monitoramento)
    case "FABRICANTE":
    case "REVENDA":
      return "/minha-agenda";
    default:
      return "/";
  }
}